import React from 'react';
import { View, StyleSheet, ScrollView, Image, Dimensions } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { RootStackParamList } from '../../types/navigation';
import { useTheme } from '../../hooks/useTheme';
import DetailContainer from '../../components/shared/DetailContainer';
import Text from '../../components/common/Text';
import { Button } from '../../components';
import { spacing, radii, shadows, fontSize, fontWeight } from '../../themes';

type VirtualDrapeDetailNavigationProp =
  NativeStackNavigationProp<RootStackParamList>;

const { width: screenWidth } = Dimensions.get('window');

const VirtualDrapeDetailScreen = () => {
  const navigation = useNavigation<VirtualDrapeDetailNavigationProp>();
  const { colors } = useTheme();

  const handleStartDrapeTest = () => {
    navigation.navigate('MainTabs', { initialTab: 'VirtualDrapeTest' });
  };

  const keyFeatures = [
    {
      icon: 'camera-outline',
      title: 'Real-Time AR',
      description:
        'See colors drape over your body in real-time using advanced augmented reality technology',
    },
    {
      icon: 'color-filter-outline',
      title: 'Color Matching',
      description:
        'Test hundreds of colors instantly and see which ones enhance your natural beauty',
    },
    {
      icon: 'layers-outline',
      title: 'Multiple Fabrics',
      description:
        'Try different fabric textures and see how they interact with light and your skin tone',
    },
    {
      icon: 'bookmark-outline',
      title: 'Save Favorites',
      description:
        'Save your best colors and build a personalized collection for future shopping',
    },
    {
      icon: 'analytics-outline',
      title: 'Instant Feedback',
      description:
        'Get immediate feedback on how each color affects your overall appearance',
    },
    {
      icon: 'share-outline',
      title: 'Share Results',
      description:
        'Share your favorite color combinations with friends and stylists for feedback',
    },
  ];

  const steps = [
    {
      step: '01',
      title: 'Position Camera',
      description: 'Stand in good lighting and position the camera',
    },
    {
      step: '02',
      title: 'Select Colors',
      description: 'Choose from our extensive color palette',
    },
    {
      step: '03',
      title: 'See Magic',
      description: 'Watch colors drape over you in real-time',
    },
  ];

  const benefits = [
    'No need to visit stores for color testing',
    'Try unlimited colors risk-free',
    'Perfect lighting simulation',
    'Save money on color consultations',
    'Build confidence in color choices',
  ];

  // Success green with 10% opacity for icon backgrounds
  const successIconBg = `${colors.success}1A`;

  return (
    <DetailContainer style={{ backgroundColor: colors.background }}>
      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
      >
        {/* Hero Section */}
        <LinearGradient
          colors={[colors.success, colors.linearGradient3]}
          style={styles.heroSection}
        >
          <View style={styles.heroContent}>
            <Text
              size="3xl"
              weight="extrabold"
              align="center"
              style={[styles.heroTitle, { color: colors.background }]}
            >
              Try Colors Virtually with AR
            </Text>
            <Text size="md" align="center" style={styles.heroSubtitle}>
              Experience the future of color testing with our revolutionary
              augmented reality drape technology
            </Text>
          </View>
        </LinearGradient>

        {/* Product Demo Images */}
        <View style={styles.demoSection}>
          <Text
            size="2xl"
            weight="bold"
            align="center"
            style={[styles.sectionTitle, { color: colors.text }]}
          >
            Experience Virtual Draping
          </Text>
          <View style={styles.demoGrid}>
            {['AR Camera View', 'Color Selection', 'Live Results'].map(
              (label, i) => (
                <View key={label} style={styles.demoImageContainer}>
                  <Image
                    source={{
                      uri: `https://picsum.photos/300/500?random=${5 + i}`,
                    }}
                    style={styles.demoImage}
                    resizeMode="cover"
                  />
                  <Text
                    size="sm"
                    weight="medium"
                    style={{ color: colors.text }}
                  >
                    {label}
                  </Text>
                </View>
              )
            )}
          </View>
        </View>

        {/* How It Works */}
        <View style={styles.section}>
          <Text
            size="2xl"
            weight="bold"
            align="center"
            style={[styles.sectionTitle, { color: colors.text }]}
          >
            How It Works
          </Text>
          <View style={styles.stepsContainer}>
            {steps.map((step, index) => (
              <View key={index} style={styles.stepItem}>
                <View
                  style={[
                    styles.stepNumber,
                    { backgroundColor: colors.success },
                  ]}
                >
                  <Text
                    size="md"
                    weight="semibold"
                    style={{ color: colors.background }}
                  >
                    {step.step}
                  </Text>
                </View>
                <View style={styles.stepContent}>
                  <Text
                    size="lg"
                    weight="semibold"
                    style={[styles.stepTitle, { color: colors.text }]}
                  >
                    {step.title}
                  </Text>
                  <Text
                    size="sm"
                    style={{ color: colors.secondary, lineHeight: 20 }}
                  >
                    {step.description}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        {/* Key Features */}
        <View style={[styles.section, { backgroundColor: colors.card }]}>
          <Text
            size="2xl"
            weight="bold"
            align="center"
            style={[styles.sectionTitle, { color: colors.text }]}
          >
            Revolutionary Features
          </Text>
          <View style={styles.featuresGrid}>
            {keyFeatures.map((feature, index) => (
              <View
                key={index}
                style={[
                  styles.featureCard,
                  {
                    backgroundColor: colors.background,
                    shadowColor: colors.text,
                  },
                ]}
              >
                <View
                  style={[
                    styles.featureIcon,
                    { backgroundColor: successIconBg },
                  ]}
                >
                  <Ionicons
                    name={feature.icon as keyof typeof Ionicons.glyphMap}
                    size={24}
                    color={colors.success}
                  />
                </View>
                <Text
                  size="sm"
                  weight="semibold"
                  align="center"
                  style={[styles.featureTitle, { color: colors.text }]}
                >
                  {feature.title}
                </Text>
                <Text
                  size="xs"
                  align="center"
                  style={{ color: colors.secondary, lineHeight: 16 }}
                >
                  {feature.description}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* Benefits Section */}
        <View style={styles.section}>
          <Text
            size="2xl"
            weight="bold"
            align="center"
            style={[styles.sectionTitle, { color: colors.text }]}
          >
            Why Choose Virtual Draping?
          </Text>
          <View
            style={[
              styles.benefitsCard,
              { backgroundColor: colors.card, shadowColor: colors.text },
            ]}
          >
            <Image
              source={{ uri: 'https://picsum.photos/400/250?random=8' }}
              style={styles.benefitsImage}
              resizeMode="cover"
            />
            <View style={styles.benefitsContent}>
              <Text
                size="xl"
                weight="semibold"
                style={[styles.benefitsTitle, { color: colors.text }]}
              >
                Save Time & Money
              </Text>
              <View style={styles.benefitsList}>
                {benefits.map((benefit, index) => (
                  <View key={index} style={styles.benefitItem}>
                    <Ionicons
                      name="checkmark-circle"
                      size={16}
                      color={colors.success}
                    />
                    <Text
                      size="sm"
                      style={[styles.benefitText, { color: colors.text }]}
                    >
                      {benefit}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          </View>
        </View>

        {/* Technology Section */}
        <View style={[styles.section, { backgroundColor: colors.card }]}>
          <Text
            size="2xl"
            weight="bold"
            align="center"
            style={[styles.sectionTitle, { color: colors.text }]}
          >
            Powered by Advanced Technology
          </Text>
          <View style={styles.techGrid}>
            {[
              {
                icon: 'hardware-chip-outline',
                title: 'AI Recognition',
                desc: 'Advanced facial recognition and skin tone analysis',
              },
              {
                icon: 'eye-outline',
                title: 'AR Rendering',
                desc: 'Real-time augmented reality color projection',
              },
            ].map((tech) => (
              <View
                key={tech.title}
                style={[
                  styles.techCard,
                  {
                    backgroundColor: colors.background,
                    shadowColor: colors.text,
                  },
                ]}
              >
                <Ionicons
                  name={tech.icon as keyof typeof Ionicons.glyphMap}
                  size={32}
                  color={colors.success}
                />
                <Text
                  size="md"
                  weight="semibold"
                  align="center"
                  style={[styles.techTitle, { color: colors.text }]}
                >
                  {tech.title}
                </Text>
                <Text
                  size="xs"
                  align="center"
                  style={{ color: colors.secondary, lineHeight: 16 }}
                >
                  {tech.desc}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* CTA Section */}
        <View style={styles.ctaSection}>
          <LinearGradient
            colors={[colors.success, colors.linearGradient3]}
            style={styles.ctaGradient}
          >
            <Text
              size="2xl"
              weight="bold"
              align="center"
              style={[styles.ctaTitle, { color: colors.background }]}
            >
              Ready to Try Virtual Draping?
            </Text>
            <Text size="md" align="center" style={styles.ctaSubtitle}>
              See the magic of AR color testing today
            </Text>
            <Button
              title="Start Virtual Test"
              onPress={handleStartDrapeTest}
              variant="primary"
              style={{
                ...styles.ctaButton,
                backgroundColor: colors.background,
              }}
              textStyle={{ ...styles.ctaButtonText, color: colors.success }}
              hoverColor={colors.border}
              trackClick={true}
              trackingLocation="virtual-drape-detail-screen"
              trackingData={{
                button_name: 'Start Virtual Test Detail Screen',
                button_type: 'start_virtual_test_detail_screen',
                screen: 'virtual_drape_detail',
              }}
            />
          </LinearGradient>
        </View>
      </ScrollView>
    </DetailContainer>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  heroSection: {
    paddingHorizontal: spacing[5],
    paddingVertical: spacing[10],
    alignItems: 'center',
  },
  heroContent: {
    maxWidth: 500,
    alignItems: 'center',
  },
  heroTitle: {
    marginBottom: spacing[4],
    fontSize: fontSize['3xl'],
    fontWeight: fontWeight.extrabold,
  },
  heroSubtitle: {
    color: 'rgba(255, 255, 255, 0.9)',
    lineHeight: 24,
  },
  demoSection: {
    paddingVertical: spacing[7],
  },
  demoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: spacing[4],
  },
  demoImageContainer: {
    alignItems: 'center',
    width: (screenWidth - 60) / 2,
    maxWidth: 160,
  },
  demoImage: {
    width: '100%',
    height: 300,
    borderRadius: radii.lg,
    marginBottom: spacing[2],
  },
  section: {
    paddingHorizontal: spacing[5],
    paddingVertical: spacing[7],
  },
  sectionTitle: {
    marginBottom: spacing[6],
  },
  stepsContainer: {
    gap: spacing[5],
  },
  stepItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  stepNumber: {
    width: 40,
    height: 40,
    borderRadius: radii.full,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacing[4],
    flexShrink: 0,
  },
  stepContent: {
    flex: 1,
  },
  stepTitle: {
    marginBottom: spacing[1],
  },
  featuresGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing[4],
    justifyContent: 'center',
  },
  featureCard: {
    width: (screenWidth - 60) / 2,
    maxWidth: 160,
    padding: spacing[4],
    borderRadius: radii.lg,
    alignItems: 'center',
    ...shadows.md,
  },
  featureIcon: {
    width: 48,
    height: 48,
    borderRadius: radii.full,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing[3],
  },
  featureTitle: {
    marginBottom: spacing[2],
  },
  benefitsCard: {
    borderRadius: radii.xl,
    overflow: 'hidden',
    ...shadows.lg,
  },
  benefitsImage: {
    width: '100%',
    height: 200,
  },
  benefitsContent: {
    padding: spacing[5],
  },
  benefitsTitle: {
    marginBottom: spacing[4],
  },
  benefitsList: {
    gap: spacing[2],
  },
  benefitItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  benefitText: {
    marginLeft: spacing[2],
  },
  techGrid: {
    flexDirection: 'row',
    gap: spacing[4],
    justifyContent: 'center',
  },
  techCard: {
    flex: 1,
    maxWidth: 150,
    padding: spacing[5],
    borderRadius: radii.lg,
    alignItems: 'center',
    ...shadows.md,
  },
  techTitle: {
    marginTop: spacing[3],
    marginBottom: spacing[2],
  },
  ctaSection: {
    paddingHorizontal: spacing[5],
    paddingVertical: spacing[7],
  },
  ctaGradient: {
    borderRadius: radii.xl,
    paddingVertical: spacing[10],
    paddingHorizontal: spacing[5],
    alignItems: 'center',
  },
  ctaTitle: {
    marginBottom: spacing[2],
  },
  ctaSubtitle: {
    color: 'rgba(255, 255, 255, 0.9)',
    marginBottom: spacing[6],
  },
  ctaButton: {
    paddingVertical: spacing[4],
    paddingHorizontal: spacing[8],
    borderRadius: radii.lg,
  },
  ctaButtonText: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.semibold,
  },
});

export default VirtualDrapeDetailScreen;
